import { BexioContact } from '@/lib/bexio-schema'
import { boolean, index, integer, pgEnum, pgTable, serial, text, timestamp, unique } from 'drizzle-orm/pg-core'
import { user, UserBasic } from './user'

// Enums
export const contactStage = pgEnum('contact_stage', ['lead', 'customer', 'partner', 'inactive', 'member', 'supplier'])
export const contactType = pgEnum('contact_type', ['person', 'company'])
export const contactRelationType = pgEnum('contact_relation_type', [
  'contact_in_charge', // Ansprechpartner
  'customer_of', // Kunde von
  'supplier_to', // Lieferant für
  'partner', // Partner
  'subsidiary', // Tochtergesellschaft
  'parent_company', // Muttergesellschaft
  'competitor', // Wettbewerber
])
export const contactNoteType = pgEnum('contact_note_type', ['general', 'call', 'meeting', 'email', 'task'])

// Tables
export const contact = pgTable(
  'contact',
  {
    id: serial('id').primaryKey(),
    created: timestamp().defaultNow().notNull(),
    updated: timestamp().defaultNow().notNull(),
    name: text('name'),
    name_add: text('name_add'),
    street: text('street'),
    house_number: text('house_number'),
    zip: text('zip'),
    city: text('city'),
    email: text('email'),
    phone: text('phone'),
    type: contactType().notNull().default('person'),
    external_contact_id: text('external_contact_id').unique(),
    owner: integer('owner').references(() => user.id),
    stage: contactStage(),
  },
  (t) => ({
    externalContactIdIdx: index('contact_external_contact_id_idx').on(t.external_contact_id),
    ownerIdIdx: index('contact_owner_idx').on(t.owner),
    stageIdx: index('contact_stage_idx').on(t.stage),
  })
)

export const contactRelation = pgTable(
  'contact_relation',
  {
    id: serial('id').primaryKey(),
    created: timestamp().defaultNow().notNull(),
    updated: timestamp().defaultNow().notNull(),
    contact_id: integer('contact_id')
      .references(() => contact.id)
      .notNull(),
    related_contact_id: integer('related_contact_id')
      .references(() => contact.id)
      .notNull(),
    relation_type: contactRelationType().notNull(),
    notes: text('notes'),
  },
  (t) => ({
    uniqueRelation: unique('contact_relations_unique').on(t.contact_id, t.related_contact_id, t.relation_type),
    contactIdIdx: index('contact_relations_contact_id_idx').on(t.contact_id),
    relatedContactIdIdx: index('contact_relations_related_contact_id_idx').on(t.related_contact_id),
  })
)

export const contactNote = pgTable(
  'contact_note',
  {
    id: serial('id').primaryKey(),
    created: timestamp().defaultNow().notNull(),
    updated: timestamp().defaultNow().notNull(),
    contact_id: integer('contact_id')
      .references(() => contact.id)
      .notNull(),
    author_id: integer('author_id')
      .references(() => user.id)
      .notNull(),
    note: text('note').notNull(),
    type: contactNoteType().notNull().default('general'),
    is_pinned: boolean('is_pinned').notNull().default(false),
  },
  (t) => ({
    contactIdIdx: index('contact_notes_contact_id_idx').on(t.contact_id),
    authorIdIdx: index('contact_notes_author_id_idx').on(t.author_id),
    typeIdx: index('contact_notes_type_idx').on(t.type),
    createdIdx: index('contact_notes_created_idx').on(t.created),
  })
)

export type Contact = typeof contact.$inferSelect & {
  relations?: ContactRelation[]
  notes?: ContactNote[]
  _owner?: UserBasic
  externalContact?: BexioContact
}

export type ContactBasic = Pick<Contact, 'id' | 'name' | 'name_add' | 'email' | 'phone' | 'type' | 'stage'>

export type ContactRelation = typeof contactRelation.$inferSelect & {
  relatedContact: ContactBasic
}
export type ContactNote = typeof contactNote.$inferSelect & {
  author: UserBasic
}

export type InsertContact = typeof contact.$inferInsert
export type InsertContactRelation = typeof contactRelation.$inferInsert
export type InsertContactNote = typeof contactNote.$inferInsert
export type UpdateContact = Partial<Contact>
